import { computed, onScopeDispose, ref } from 'vue'
import type { AppTableSortModel, ListSort } from '@staff/ui'

import { searchUsers, type UserSearchResultItem } from '@/services/user'
import dayjs from 'dayjs'

export type UserListFilters = {
  search: string
}

export type UserListSortField = 'name' | 'personalNumber'

export type UsersTableModel = {
  items: UserSearchResultItem[]
  loading: boolean
  emptyMessage: string
  totalItems: number
  sort: AppTableSortModel<UserListSortField>
}

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_FILTERS: UserListFilters = { search: '' }

// Never let an empty/near-empty query fetch every user - require a real search term first.
export const MIN_SEARCH_LENGTH = 3

// No pagination - just show the closest matches.
export const MAX_RESULTS = 30

const collator = new Intl.Collator('sv')

const fullName = (user: UserSearchResultItem) => [user.givenName, user.surname].filter(Boolean).join(' ')

const SHORT_PERSONAL_NUMBER = /^(\d{6})-(\d{4})$/
const LONG_PERSONAL_NUMBER = /^(\d{8})-(\d{4})$/

// Personal numbers are stored without a dash, and the short form (no century) defaults to 19xx.
const normalizeSearchTerm = (term: string): string => {
  const shortMatch = term.match(SHORT_PERSONAL_NUMBER)
  if (shortMatch) {
    // Determine the century prefix for the short personal number based on whether it is before or after 100 years ago.
    const hundredYearsAgo = dayjs().subtract(100, 'years').format('YYMMDD') // ie 260423 for 100 years ago from April 23, 2026
    const prefix = shortMatch[1] < hundredYearsAgo ? '20' : '19' // If less than ie 250423 (100 years ago), use 20xx, otherwise 19xx
    return `${prefix}${shortMatch[1]}${shortMatch[2]}`
  }

  const longMatch = term.match(LONG_PERSONAL_NUMBER)
  if (longMatch) return `${longMatch[1]}${longMatch[2]}`

  return term
}

export const useUserSearch = () => {
  const filters = ref<UserListFilters>({ ...DEFAULT_FILTERS })
  const users = ref<UserSearchResultItem[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  // Total matches reported by the API before the MAX_RESULTS cap, so the UI can
  // show that a search matched more users than the table displays.
  const totalMatches = ref(0)
  const sort = ref<ListSort<UserListSortField> | null>({ field: 'name', direction: 'asc' })

  let activeRequestToken = 0
  let debounceTimer: ReturnType<typeof setTimeout> | undefined

  const hasMinimumSearchLength = computed(() => filters.value.search.trim().length >= MIN_SEARCH_LENGTH)

  const search = async () => {
    const token = ++activeRequestToken

    if (!hasMinimumSearchLength.value) {
      error.value = null
      isLoading.value = false
      users.value = []
      totalMatches.value = 0
      return
    }

    try {
      error.value = null
      isLoading.value = true

      const result = await searchUsers(normalizeSearchTerm(filters.value.search.trim()))

      if (token !== activeRequestToken) return

      totalMatches.value = result.length
      users.value = result.slice(0, MAX_RESULTS)
    } catch (err) {
      if (token !== activeRequestToken) return

      error.value = err instanceof Error ? err : new Error(String(err))
      users.value = []
      totalMatches.value = 0
    } finally {
      if (token === activeRequestToken) {
        isLoading.value = false
      }
    }
  }

  // Typing hits the API, so hold off until the user pauses.
  const setSearch = (value: string) => {
    if (filters.value.search === value) return

    filters.value = { ...filters.value, search: value }
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => void search(), SEARCH_DEBOUNCE_MS)
  }

  const toggleSort = (field: UserListSortField) => {
    const nextDirection = sort.value?.field === field && sort.value.direction === 'asc' ? 'desc' : 'asc'
    sort.value = { field, direction: nextDirection }
  }

  onScopeDispose(() => clearTimeout(debounceTimer))

  const sortedUsers = computed(() => {
    const currentSort = sort.value
    if (!currentSort) return users.value

    const direction = currentSort.direction === 'desc' ? -1 : 1

    return [...users.value].sort((a, b) => {
      const comparison =
        currentSort.field === 'name'
          ? collator.compare(fullName(a), fullName(b))
          : collator.compare(a.personalNumber, b.personalNumber)

      return comparison * direction
    })
  })

  const tableModel = computed<UsersTableModel>(() => ({
    items: sortedUsers.value,
    loading: isLoading.value,
    emptyMessage: hasMinimumSearchLength.value
      ? 'Inga användare hittades.'
      : `Skriv minst ${MIN_SEARCH_LENGTH} tecken för att söka.`,
    totalItems: totalMatches.value,
    sort: {
      field: sort.value?.field ?? null,
      direction: sort.value?.direction ?? null,
      toggle: toggleSort,
    },
  }))

  return {
    setSearch,
    filters,
    searchUserError: error,
    tableModel,
  }
}
